import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Sparkles, Camera, Globe, Zap, Users, Target,
  ArrowRight, CheckCircle2, TrendingUp, Shield, Heart,
  Building2, Languages, Wand2, Star,
} from 'lucide-react';

const stats = [
  { value: '16+', label: 'Languages Supported', icon: Globe },
  { value: '10', label: 'AI Enhancement Rules', icon: Wand2 },
  { value: '4', label: 'Marketplace Formats', icon: Building2 },
  { value: '24/7', label: 'AI-Powered Platform', icon: Zap },
];

const values = [
  {
    icon: Target,
    title: 'Agent-First Design',
    description: 'Every feature is built from the perspective of a working real estate agent who needs results fast — not a tech demo.',
  },
  {
    icon: Globe,
    title: 'Global by Default',
    description: 'From day one, ListBright was designed for international markets. Multi-language, multi-currency, and culturally-aware descriptions.',
  },
  {
    icon: Shield,
    title: 'Quality Without Compromise',
    description: 'Our AI enhancement rules are non-negotiable. Consistent, professional results every time — across every photo in a batch.',
  },
  {
    icon: Heart,
    title: 'Empowering, Not Replacing',
    description: 'AI handles the repetitive work so agents can focus on what they do best: building relationships and closing deals.',
  },
];

const timeline = [
  {
    title: 'The Problem',
    description: 'Real estate agents spend hours crafting listings — writing descriptions, editing photos, translating content. Time that could be spent with clients.',
  },
  {
    title: 'The Idea',
    description: 'What if AI could handle all of that in minutes? Not generic templates, but truly intelligent, property-aware content that sells.',
  },
  {
    title: 'The Platform',
    description: 'ListBright was born — combining vision AI, natural language generation, and photo enhancement into one seamless workflow.',
  },
  {
    title: 'The Future',
    description: 'We\'re expanding into new markets, adding more languages, and continuously improving our AI to deliver even better results.',
  },
];

const features = [
  { icon: Camera, label: 'AI Photo Enhancement', description: '10 professional rules applied to every photo automatically' },
  { icon: Languages, label: 'Multi-Language Descriptions', description: 'Generate listings in 16+ languages simultaneously' },
  { icon: Star, label: 'Premium Descriptions', description: 'Research-backed prose that boosts clicks by 45%' },
  { icon: Sparkles, label: 'Virtual Staging', description: 'Transform empty rooms into furnished spaces with AI' },
  { icon: TrendingUp, label: 'Listing Score', description: 'AI-powered analysis to optimize your listing performance' },
  { icon: Users, label: 'Team Collaboration', description: 'Share credits and templates across your agency' },
];

export default function About() {
  return (
    <Layout>
      <PageMeta
        title="About ListBright — AI Suite for Real Estate Listings"
        description="ListBright is the all-in-one AI suite for real estate listings — photos, virtual staging, descriptions, video, microsite and flyer."
        breadcrumbs={[{ label: 'About', path: '/about' }]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'ListBright',
          url: 'https://listbright.ai',
          logo: 'https://listbright.ai/logo.png',
          description: 'The all-in-one AI suite for real estate listings: photos, virtual staging, descriptions, video, microsite and PDF flyer — from a single upload.',
          sameAs: [
            'https://facebook.com/listbright',
            'https://instagram.com/listbright',
            'https://twitter.com/listbright',
            'https://linkedin.com/company/listbright',
          ],
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container max-w-5xl mx-auto px-4 relative">
          <div className="text-center space-y-6">
            <Badge variant="outline" className="px-4 py-1.5 text-sm">
              About ListBright
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              The all-in-one AI suite for{' '}
              <span className="text-primary">real estate listings</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Photos, virtual staging, descriptions, video, microsite and flyer — every asset a
              listing needs, generated from a single upload. Built for agents, brokers and owners
              worldwide.
            </p>
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button asChild size="lg">
                <Link to="/pricing">
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="text-center space-y-2">
                  <Icon className="w-6 h-6 mx-auto text-primary" />
                  <p className="text-3xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Our Story</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">
              From Frustration to Innovation
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {timeline.map((item, index) => (
              <Card key={item.title} className="relative overflow-hidden group hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 md:py-28 bg-muted/20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">What We Do</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">
              Everything You Need to List Smarter
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              ListBright combines multiple AI technologies into one platform purpose-built for real estate.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.label} className="group hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 space-y-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold">{feature.label}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-28">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Our Values</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <div key={value.title} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{value.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary/5">
        <div className="container max-w-3xl mx-auto px-4 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Transform Your Listings?
          </h2>
          <p className="text-muted-foreground text-lg">
            Join real estate professionals worldwide who are saving hours on every listing with ListBright.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button asChild size="lg">
              <Link to="/pricing">
                <Sparkles className="w-4 h-4 mr-2" />
                Start Free
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/features">
                Explore Features <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
